import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CLOUD_DATA_HYDRATED_EVENT } from '../lib/cloud/reconcile';

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({ theme: 'dark', toggle: () => {} });

// Apply the theme class to <html> immediately (called at module load to prevent flash)
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.remove('dark', 'light');
  root.classList.add(theme);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('mycryptoplanner_theme') as Theme | null;
    return stored === 'light' ? 'light' : 'dark';
  });

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('mycryptoplanner_theme', theme);
  }, [theme]);

  // Theme is read synchronously from localStorage on mount (no flash). After the
  // login reconcile hydrates cloud preferences into localStorage, re-read so the
  // cloud value (source of truth) takes effect without a reload.
  useEffect(() => {
    const reread = () => {
      const stored = localStorage.getItem('mycryptoplanner_theme') as Theme | null;
      setTheme(stored === 'light' ? 'light' : 'dark');
    };
    window.addEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
    return () => window.removeEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
  }, []);

  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);

  return (
    <ThemeContext.Provider value={{ theme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
